/**
 * Cross-links between the social mockup generators. Each tool page filters
 * itself out of this list, so adding a new generator here links it everywhere.
 *
 * ⚠️ THIS IS THE FAMILY'S MOST VALUABLE INTERNAL LINK. One entry here puts a
 * tool in the related-tools strip on every other mockup page, i.e. linked from
 * pages earning roughly 109,000 clicks per 28 days. The free-tools CATEGORY hub
 * pages, by contrast, earned 5 clicks in total over the same window, so this
 * list matters and the hub does not.
 */
export interface IMockupToolLink {
  slug: string;
  name: string;
  description: string;
  /**
   * Full path, for a tool that does NOT live under `/free-tools/social-media/`.
   * Optional on purpose: every entry that shipped before this field existed
   * keeps its exact behaviour through the fallback in `toolHref` below, so
   * adding this could not change a single live link.
   */
  href?: string;
}

/**
 * Where a related-tool card points. The default is the social-media category
 * because that is where the whole mockup family lives; an entry only needs an
 * explicit `href` if it is filed somewhere else.
 */
export const toolHref = (tool: { slug: string; href?: string }): string =>
  tool.href || `/free-tools/social-media/${tool.slug}`;

export const MOCKUP_TOOL_LINKS: IMockupToolLink[] = [
  {
    slug: 'fake-whatsapp-chat-generator',
    name: 'Free Fake WhatsApp Chat Generator',
    description:
      'Build a realistic WhatsApp-style conversation mockup with bubbles, ticks, and timestamps. Download a PNG.',
  },
  {
    slug: 'fake-telegram-chat-generator',
    name: 'Free Fake Telegram Chat Generator',
    description:
      'Design a Telegram-style chat mockup in light or dark mode and export it as a clean PNG.',
  },
  {
    slug: 'fake-messenger-chat-generator',
    name: 'Free Fake Messenger Chat Generator',
    description:
      'Create a Messenger-style conversation mockup with blue pill bubbles. Free, in your browser.',
  },
  {
    slug: 'fake-reddit-post-generator',
    name: 'Free Fake Reddit Post Generator',
    description:
      'Mock up a Reddit-style post with votes, awards, and a threaded comment section. Export a PNG.',
  },
  {
    slug: 'fake-tweet-generator',
    name: 'Free Fake Tweet & X Post Generator',
    description:
      'Create realistic X (Twitter) post screenshots with views, checkmark tiers, and bookmarks. Download a clean PNG.',
  },
  {
    slug: 'fake-imessage-generator',
    name: 'Free Fake Text Message Generator',
    description:
      'Build an iPhone text message mockup with blue iMessage or green SMS bubbles, reactions, and a Read receipt.',
  },
  {
    slug: 'fake-facebook-post-generator',
    name: 'Free Fake Facebook Post Generator',
    description:
      'Mock up a Facebook post with reactions, shares, and a comment thread. Export a PNG.',
  },
  {
    slug: 'fake-instagram-post-generator',
    name: 'Free Fake Instagram Post Generator',
    description:
      'Design an Instagram post mockup around your own photo, with caption, likes, and comments.',
  },
  {
    slug: 'fake-instagram-dm-generator',
    name: 'Free Fake Instagram DM Generator',
    description:
      'Build an Instagram DM mockup with gradient bubbles, heart reactions, and a Seen label.',
  },
  {
    slug: 'fake-snapchat-generator',
    name: 'Free Fake Snapchat Generator',
    description:
      'Create a Snapchat chat mockup with colored side bars, a snap score, and a status on every row.',
  },
  {
    slug: 'text-behind-image',
    name: 'Free Text Behind Image Maker',
    description:
      'Place bold text behind your subject in one click. Automatic cutout, no sign-up, all in your browser.',
  },
  {
    slug: 'fake-chatgpt-generator',
    name: 'Free Fake ChatGPT Generator',
    description:
      'Build a ChatGPT-style conversation mockup in light or dark mode, with reasoning rows and code blocks.',
  },
  {
    slug: 'fake-gemini-generator',
    name: 'Free Fake Gemini Generator',
    description:
      'Design a Gemini-style AI chat mockup with a sparkle mark, bolded lead-ins, and a clean PNG export.',
  },
  {
    slug: 'fake-ai-overview-generator',
    name: 'Free Fake Google AI Overview Generator',
    description:
      'Mock up a Google AI Overview card with a search bar, bulleted answer, and cited source chips.',
  },
];
